import { Typography, useTheme } from "@mui/material";
import {
  AppConstants,
  conceptIDs,
  MLY_IDS,
  sharedStateKeys,
  stepTypes,
} from "../../../../constants/constant";
import { Step } from "../../../../Interface/scheduler/form";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";
import { useFormContext } from "../../../../common/contexts/FormContext";
import "./StepTitle.css";
import { CalendarTitleDetails } from "../../../servicePricingTabs/components/ServiceTabDetails.interface";

const StepTitle = ({ currentStep }: { currentStep?: Step }) => {
  const { state } = useSharedStateWithActions();
  const { formValues } = useFormContext();
  const theme = useTheme();
  const isCalendarPage =
    currentStep?.steptype === AppConstants.REQUEST_TIME_TYPE;
  const isQuotePage =
    currentStep?.steptype === stepTypes.STEP_PRICE_QUOTE_ESTIMATION;
  const isPricingAvailable = state?.[sharedStateKeys.IS_PRICING_AVAILABLE];
  const isPricingConfig = formValues.isPricingConfig;

  const commonTextStyles = {
    fontSize: "1rem",
    lineHeight: "1.5rem",
    margin: "10px auto",
    color: "rgb(55 65 81)",
  };

  const getWDGTitle = () => {
    if (isCalendarPage) {
      if (isPricingAvailable) {
        return currentStep.title;
      } else {
        return currentStep.estimateTitle;
      }
    }
    return currentStep.title;
  };

  const getWDGSubTitle = () => {
    if (isCalendarPage) {
      if (isPricingAvailable) {
        return currentStep.subTitle;
      } else {
        return currentStep.estimateSubTitle;
      }
    }
    return currentStep.subTitle;
  };

  const getWDGSubTitleClass = () => {
    if (isQuotePage && state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE]) {
      return "hide";
    } else {
      return "legal-text";
    }
  };

  const shouldShowMLYEstimate = () => {
    const hasValidEstimate =
      formValues?.mlyEstimate &&
      typeof formValues.mlyEstimate === "string" &&
      !formValues.mlyEstimate.includes("$0");

    const isEstimateEnabled =
      formValues?.isMLYEstimate && !formValues.hideCESEstimation;

    const isStepConfigValid =
      isCalendarPage &&
      !currentStep?.[formValues?.[MLY_IDS.FREQUENCY]]?.hideEstimateSection;

    return hasValidEstimate && isEstimateEnabled && isStepConfigValid;
  };

  const getMLYEstimate = () => {
    if (shouldShowMLYEstimate()) {
      return (
        <>
          <Typography
            variant="h2"
            gutterBottom
            id="mly-estimate-title"
            className="mly-estimate-label"
          >
            {currentStep?.[formValues?.[MLY_IDS.FREQUENCY]]?.estimateTitle}
          </Typography>
          <Typography
            variant="h2"
            gutterBottom
            id="mly-estimate-title"
            className="mly-estimate"
          >
            {formValues?.mlyEstimate}
          </Typography>
          <p className="estimate-disclaimer">
            {currentStep?.[formValues?.[MLY_IDS.FREQUENCY]]?.estimateDisclaimer}
          </p>
        </>
      );
    }
  };

  const getJUKDisclaimer = (calendarDetails: CalendarTitleDetails) => {
    if (isCalendarPage) {
      return (
        <>
          <Typography
            className="juk-title"
            paragraph
            sx={{ ...commonTextStyles, fontWeight: 400 }}
            id="juk-sub-title"
          >
            {calendarDetails.disclaimer}
          </Typography>
          <Typography
            className="juk-title"
            paragraph
            sx={{ ...commonTextStyles, fontWeight: 600 }}
            id="disclaimer-text"
          >
            {calendarDetails.subTitle}
          </Typography>
        </>
      );
    }
  };

  const getTitle = () => {
    if (isPricingConfig && isCalendarPage) {
      return formValues.calendarTitleDetails.title;
    } else if (currentStep?.typeOfCleaningEnabled) {
      return currentStep?.[formValues?.[MLY_IDS.FREQUENCY]]?.title;
    }
    return currentStep?.title;
  };

  const getServiceTitle = () => {
    if (currentStep?.typeOfCleaningEnabled) {
      return (
        <Typography
          paragraph
          sx={{ ...commonTextStyles, fontWeight: 400 }}
          id="mly-service-title"
        >
          {currentStep?.[formValues?.[MLY_IDS.FREQUENCY]]?.subTitle}
        </Typography>
      );
    }
  };

  const renderExistingTitle =
    currentStep?.existingCusTitle && formValues.existingcustomer === "true";

  const renderTypography = () =>
    currentStep?.titleSwap ||
    formValues.conceptID === conceptIDs.WDG_CONCEPT_ID ? (
      <>
        <Typography
          variant="h2"
          gutterBottom
          id="wdg-step-heading"
          className="step-heading"
        >
          {getWDGTitle()}
        </Typography>
        <Typography
          className={getWDGSubTitleClass()}
          id="wdg-legal-text"
          paragraph
          sx={commonTextStyles}
        >
          {getWDGSubTitle()}
        </Typography>
        {renderExistingTitle && (
          <Typography
            className="legal-text existing-title-text"
            id="wdg-legal-text-existing-cus"
            paragraph
            sx={commonTextStyles}
          >
            {currentStep?.existingCusTitle}
          </Typography>
        )}
      </>
    ) : (
      <>
        <Typography
          className="legal-text"
          paragraph
          sx={{
            ...commonTextStyles,
            fontWeight: 600,
            color: currentStep?.enableLegalTextBrandColor
              ? theme.palette.secondary.main
              : "rgb(55 65 81)",
          }}
          id="legal-text"
        >
          {currentStep?.subTitle}
        </Typography>
        <Typography
          variant="h2"
          gutterBottom
          className="step-heading"
          id={currentStep?.id}
        >
          {getTitle()}
        </Typography>
        {getMLYEstimate()}
        {getServiceTitle()}
        {isPricingConfig && getJUKDisclaimer(formValues.calendarTitleDetails)}
      </>
    );

  return <>{renderTypography()}</>;
};

export default StepTitle;
